// src/components/TickerRow/TickerRow.js

import { useState, useEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom"; 
// 1. 기존 데이터 삭제 및 신규 데이터 임포트
import globalTickers from "../../data/global_tickers_min.json"; 
import "./TickerRow.css";
import loadingStyles from './../../styles/Common.module.css';
import { useTranslation } from "react-i18next";
import { getAllTickers, validateTickerWithAPI, isValidTickerLocal } from "../../utils/tickerValidator";

export default function TickerRow({
  value,
  onChange,
  className,
  existingTickers = [],
  disableDefaultStyles = false,
  style = {},
}) {
  const [query, setQuery] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const ignoreBlurRef = useRef(false);
  const suggestionListRef = useRef(null);
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const componentRef = useRef(null); 
  const [inputRect, setInputRect] = useState(null); 

  // 2. 검색 및 유효성 검사를 위한 데이터 가공 (useMemo 사용)
  // const allTickers = useMemo(() => {
  //   return globalTickers.map(item => ({
  //     symbol: item.s.toUpperCase(),
  //     name: item.n ? item.n.toUpperCase() : "NO NAME",
  //     type: item.t || "Unknown",
  //     exchange: item.e || ""
  //   }));
  // }, []);

  const allTickers = useMemo(() => {
    const data = getAllTickers(); // 유틸리티에서 가져옴
    return data.map(item => ({
      symbol: item.s.toUpperCase(),
      name: item.n ? item.n.toUpperCase() : "NO NAME",
      type: item.t || "Unknown",
      exchange: item.e || ""
    }));
  }, []);

  const tickerSet = useMemo(() => new Set(allTickers.map(t => t.symbol)), [allTickers]);

  useEffect(() => {
    setQuery(value || "");
  }, [value]);

  // (기존 useEffect 위치 계산 및 이벤트 리스너 로직은 동일하게 유지)
  useEffect(() => {
    if (suggestionListRef.current) {
      const items = suggestionListRef.current.children;
      for (let i = 0; i < items.length; i++) {
        if (i === selectedIndex) items[i].classList.add("selected");
        else items[i].classList.remove("selected");
      }
      if (selectedIndex >= 0 && items[selectedIndex]) {
        items[selectedIndex].scrollIntoView({ block: "nearest", behavior: "instant" });
      }
    }
  }, [selectedIndex]);

  const updateInputRect = (inputElement) => {
    if (inputElement) {
        const rect = inputElement.getBoundingClientRect();
        setInputRect({
            top: rect.bottom, 
            left: rect.left,
            width: rect.width,
        });
    }
  };

  useEffect(() => {
    const inputElement = componentRef.current?.querySelector('input[type="text"]');
    if (inputElement) {
        updateInputRect(inputElement);
        const modalContent = componentRef.current.closest('div[class*="modalContent"]');
        if (modalContent) {
            const handleScrollOrResize = () => updateInputRect(inputElement);
            modalContent.addEventListener('scroll', handleScrollOrResize);
            window.addEventListener('resize', handleScrollOrResize);
            return () => {
                modalContent.removeEventListener('scroll', handleScrollOrResize);
                window.removeEventListener('resize', handleScrollOrResize);
            };
        }
    }
  }, []); 

  // 3. 입력 핸들러 수정 (새로운 데이터 구조 기준 검색)
  const handleInputChange = (e) => {
    const input = e.target.value.toUpperCase().trim();
    setQuery(input);
    setErrorMessage("");
    setSelectedIndex(-1);

    const inputElement = e.target;
    updateInputRect(inputElement);
    
    if (input.length > 0) {
      // 심볼 시작 부분 우선순위 검색
      const startsWithMatch = allTickers.filter(item => item.symbol.startsWith(input));
      const containsMatch = allTickers.filter(
        item => item.symbol.includes(input) && !item.symbol.startsWith(input)
      );
      
      setSuggestions([...startsWithMatch, ...containsMatch].slice(0, 5));
    } else {
      setSuggestions([]);
    }
  };

  const checkTickerYahoo = async (ticker) => {
    const tickerRegex = /^[A-Za-z0-9.^-]+$/;
    if (!tickerRegex.test(ticker)) return [false, ""];
    try {
      const res = await fetch(`/.netlify/functions/checkTicker?ticker=${ticker}`);
      const data = await res.json();
      return [data.valid, data.ticker];
    } catch {
      return [false, ""];
    }
  };

  // 4. 유효성 검사 함수 수정
  // 4. 유효성 검사 함수 (기존 tickerSet 활용)
  const inYFdataset = (ticker) => ticker && tickerSet.has(ticker.toUpperCase());

  const addTicker = async (ticker) => {
    if (!ticker) return;
    ticker = ticker.trim().toUpperCase();

    // 중복 체크
    if (existingTickers.includes(ticker)) {
      alert(`${ticker} ${t("addedTicker")}`);
      // 여기서도 닫아주는 것이 좋습니다 (선택 사항)
      setSuggestions([]); 
      return;
    }

    // 1. 로컬 데이터셋(inYFdataset) 우선 확인
    if (inYFdataset(ticker)) {
      onChange(ticker);
      setQuery(ticker);
      setErrorMessage("");
      
      // ✅ [수정] 로컬 성공 시에도 리스트 초기화 코드가 필요합니다!
      setSuggestions([]); 
      setSelectedIndex(-1);
      
      return; // 로직 종료
    }

    // 2. 로컬에 없으면 서버 API 호출
    try {
      setIsLoading(true);
      const verifiedTicker = await validateTickerWithAPI(ticker);
      
      if (verifiedTicker) {
        onChange(verifiedTicker);
        setQuery(verifiedTicker);
        setErrorMessage("");
      } else {
        setErrorMessage(t("notValidTicker"));
        setQuery(""); 
      }
    } finally {
      // 여기는 서버 통신이 끝났을 때만 실행됩니다.
      setIsLoading(false);
      setSuggestions([]);
      setSelectedIndex(-1);
    }
  };

  // (handleKeyDown, handleBlur 등 기존 UI 로직 유지)
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
        addTicker(suggestions[selectedIndex].symbol);
      } else if (query.length > 0) {
        addTicker(query);
      }
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex(prev => prev > 0 ? prev - 1 : suggestions.length - 1);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex(prev => prev < suggestions.length - 1 ? prev + 1 : 0);
    }
  };

  const handleBlur = () => {
    if (ignoreBlurRef.current) return;
    if (query.trim() !== "" && query !== value) {
      addTicker(query);
    } else {
      setErrorMessage("");
    }
  };

  const containerCls = disableDefaultStyles ? "" : "ticker-row-container";
  const inputCls = disableDefaultStyles ? className : `ticker-row-input ${className || ""}`;

  return (
    <div className={containerCls} style={disableDefaultStyles ? style : undefined} ref={componentRef}>
      <input
        type="text"
        value={query}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        placeholder=""
        className={inputCls}
        style={disableDefaultStyles ? style : undefined}
        disabled={isLoading}
      />

      {isLoading && (
        <div className={loadingStyles.smallRowLoadingContainer}>
          <div className={loadingStyles.spinner}></div>
          <p>{t("checkingTicker")}</p>
        </div>
      )}

      {suggestions.length > 0 && inputRect && (
        createPortal(
          <ul 
            className="ticker-row-suggestions portal-suggestions" 
            ref={suggestionListRef}
            style={{
              position: 'fixed',
              top: `${inputRect.top}px`,
              left: `${inputRect.left}px`,
              width: `${inputRect.width}px`,
              zIndex: 2000,
            }}
          >
            {suggestions.map((item, idx) => (
              <li
                key={`${item.symbol}-${idx}`}
                onMouseDown={e => { e.preventDefault(); ignoreBlurRef.current = true; }}
                onMouseUp={() => (ignoreBlurRef.current = false)}
                onMouseEnter={() => setSelectedIndex(idx)}
                onClick={() => addTicker(item.symbol)}
                className={selectedIndex === idx ? "selected" : ""}
              >
                <strong>{item.symbol}</strong> - {item.name} <small>({item.type} | {item.exchange})</small>
              </li>
            ))}
          </ul>,
          document.body
        )
      )}

      {errorMessage && (
        <div className="ticker-row-error-message">{errorMessage}</div>
      )}
    </div>
  );
}